import { CheckCircle, Eye, FileText, Mail, Send } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';
import { DocumentTemplate } from '@/services/types/letters-types';

import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { QualificationStatus } from './types';

// ---------------------------------------------------------------------------
// Retainer Preview Modal
// ---------------------------------------------------------------------------

interface RetainerPreviewModalProps {
  open: boolean;
  onClose: () => void;
  firmId: string;
  mergeFields?: Record<string, string | null>;
}

const RetainerPreviewModal: React.FC<RetainerPreviewModalProps> = ({
  open,
  onClose,
  firmId,
  mergeFields,
}) => {
  // Fetch firm-scoped retainer templates
  const { data: firmData } = useTemplateListQuery(
    { firmId, scope: 'FIRM', status: 'ACTIVE', category: 'RETAINER' },
    { skip: !open || !firmId },
  );
  const firmResults = firmData?.results ?? [];

  const { data: globalData } = useTemplateListQuery(
    { scope: 'GLOBAL', status: 'ACTIVE', category: 'RETAINER' },
    { skip: !open || firmResults.length > 0 },
  );
  const globalResults = globalData?.results ?? [];

  const templates = firmResults.length > 0 ? firmResults : globalResults;
  const hasMultiple = templates.length > 1;

  const [selectedIdx, setSelectedIdx] = useState(0);
  const selectedTemplate = (templates[selectedIdx] as DocumentTemplate) ?? null;

  useEffect(() => {
    setSelectedIdx(0);
  }, [templates.length]);

  // Use pre-rendered preview URL from the list response — instant, no render needed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const previewUrl = (selectedTemplate as any)?.preview_render_url ?? null;
  // previewUrl is null when template hasn't been pre-rendered yet

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0 bg-zinc-900 border-zinc-800 text-white">
        <DialogHeader className="px-5 py-4 border-b border-zinc-800 shrink-0">
          <DialogTitle className="text-sm font-black text-white">
            Preview Retainers
          </DialogTitle>
        </DialogHeader>

        {templates.length === 0 ? (
          /* No retainers available */
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="size-12 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
              <FileText className="size-6 text-zinc-500" />
            </div>
            <p className="text-sm font-bold text-white mb-1">No Retainer Templates</p>
            <p className="text-xs font-medium text-zinc-500 text-center max-w-[280px]">
              No retainer templates are configured for this firm. Contact your admin to set up templates in Firm Preferences.
            </p>
          </div>
        ) : (
        <div className="flex flex-1 min-h-0">
          {/* Left: Template list (only if multiple) */}
          {hasMultiple && (
            <div className="w-[200px] shrink-0 border-r border-zinc-800 overflow-y-auto bg-zinc-900 p-2 space-y-1">
              {templates.map((t, idx) => {
                const tmpl = t as DocumentTemplate;
                return (
                  <button
                    key={tmpl.id}
                    type="button"
                    onClick={() => setSelectedIdx(idx)}
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-lg text-[11px] font-semibold transition-all',
                      selectedIdx === idx
                        ? 'bg-white text-zinc-900'
                        : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
                    )}
                  >
                    {tmpl.display_name || tmpl.name}
                    {tmpl.requires_signatures && (
                      <span className="block text-[9px] font-medium mt-0.5 opacity-60">
                        Requires signatures
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Right: Preview */}
          <div className="flex-1 overflow-hidden bg-white rounded-br-lg">
            {previewUrl ? (
              <iframe
                src={`${previewUrl}#toolbar=1`}
                className="w-full h-full border-0"
                title="Retainer Preview"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full bg-zinc-950 px-6">
                <FileText className="size-8 text-zinc-700 mb-3" />
                <p className="text-xs font-bold text-zinc-400 mb-1">
                  {selectedTemplate?.display_name || selectedTemplate?.name || 'Template'}
                </p>
                <p className="text-[11px] font-medium text-zinc-600 text-center">
                  Preview not yet generated for this template. The preview will be available after the template is re-analyzed.
                </p>
              </div>
            )}
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Send Retainer Button
// ---------------------------------------------------------------------------

interface SendRetainerButtonProps {
  status: QualificationStatus;
  clientEmail: string | undefined;
  onSendRetainer: (email: string) => void;
  onEmailChange?: (email: string) => void;
  onMarkSentManually?: () => void;
  disabled?: boolean;
  className?: string;
  isRecommended?: boolean;
  /** Firm ID for fetching retainer templates (needed for preview) */
  firmId?: string;
  /** Pre-filled merge fields for template preview */
  initialMergeFields?: Record<string, string | null>;
}

export const SendRetainerButton: React.FC<SendRetainerButtonProps> = ({
  status,
  clientEmail,
  onSendRetainer,
  onEmailChange,
  onMarkSentManually,
  disabled = false,
  className,
  isRecommended = false,
  firmId,
  initialMergeFields,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverMode, setPopoverMode] = useState<'options' | 'email'>('options');
  const [email, setEmail] = useState(clientEmail || '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const prevStatusRef = useRef<QualificationStatus>(status);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (emailToValidate: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToValidate);
  };

  const hasValidEmail = !!(clientEmail && validateEmail(clientEmail));

  useEffect(() => {
    if (clientEmail) {
      setEmail(clientEmail);
    }
  }, [clientEmail]);

  useEffect(() => {
    if (showPopover && popoverMode === 'email' && emailInputRef.current) {
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 50);
    }
  }, [showPopover, popoverMode]);

  useEffect(() => {
    if (prevStatusRef.current !== 'qualified' && status === 'qualified') {
      setIsAnimating(true);
      const timeout = setTimeout(() => setIsAnimating(false), 600);
      prevStatusRef.current = status;
      return () => clearTimeout(timeout);
    }
    prevStatusRef.current = status;
    return undefined;
  }, [status]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPopoverMode('options');
    setShowPopover(true);
    setEmail(clientEmail || '');
    setEmailError(null);
  };

  const handleSendViaCMS = () => {
    if (hasValidEmail && clientEmail) {
      onSendRetainer(clientEmail);
      setShowPopover(false);
    } else {
      setPopoverMode('email');
    }
  };

  const handleMarkManually = () => {
    if (onMarkSentManually) {
      onMarkSentManually();
    }
    setShowPopover(false);
  };

  const handlePreview = () => {
    setShowPopover(false);
    setShowPreview(true);
  };

  const handleEmailInputChange = (value: string) => {
    setEmail(value);
    setEmailError(null);
    if (onEmailChange) {
      onEmailChange(value);
    }
  };

  const handleSubmitEmail = () => {
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email');
      return;
    }
    if (onEmailChange) {
      onEmailChange(email);
    }
    onSendRetainer(email);
    setShowPopover(false);
  };

  const isQualified = status === 'qualified';

  const renderOptionsView = () => (
    <div className="space-y-2">
      {/* Send in app */}
      <button
        type="button"
        onClick={handleSendViaCMS}
        className="w-full h-10 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-2 transition-colors"
      >
        <Send className="w-3.5 h-3.5" />
        {hasValidEmail ? 'Send it now' : 'Send it now...'}
      </button>


      {/* Mark Sent Manually */}
      {onMarkSentManually && (
        <button
          type="button"
          onClick={handleMarkManually}
          className="w-full h-10 px-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold flex items-center justify-center gap-2 transition-colors"
        >
          <CheckCircle className="w-3.5 h-3.5" />
          Mark as Sent Manually
        </button>
      )}


      {/* Preview Retainers */}
      {firmId && (
        <button
          type="button"
          onClick={handlePreview}
          className="w-full h-10 px-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold flex items-center justify-center gap-2 transition-colors"
        >
          <Eye className="w-3.5 h-3.5" />
          Preview Retainers
        </button>
      )}
    </div>
  );

  const renderEmailView = () => (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-zinc-500" />
          <Label htmlFor="retainer-email" className="text-sm font-medium">
            Client Email
          </Label>
        </div>
        <p className="text-xs text-zinc-500">
          Enter email to send retainer
        </p>
      </div>

      <div className="space-y-1.5">
        <Input
          ref={emailInputRef}
          id="retainer-email"
          type="email"
          placeholder="client@example.com"
          value={email}
          onChange={(e) => handleEmailInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmitEmail();
            }
          }}
          className={cn('h-8 text-sm', emailError && 'border-rose-500')}
        />
        {emailError && (
          <p className="text-xs text-rose-500">{emailError}</p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setPopoverMode('options')}
        >
          Back
        </Button>
        <Button
          size="sm"
          className="h-8 bg-emerald-600 hover:bg-emerald-700"
          onClick={handleSubmitEmail}
        >
          Send
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Popover open={showPopover} onOpenChange={setShowPopover}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={cn(
              'h-9 px-4 rounded-full text-xs font-bold transition-all flex items-center gap-1.5',
              disabled && 'bg-zinc-800/60 text-zinc-600 cursor-not-allowed',
              (isQualified || isRecommended) &&
                !disabled &&
                'bg-emerald-500 text-white hover:bg-emerald-400 active:scale-95',
              !isQualified &&
                !isRecommended &&
                hasValidEmail &&
                !disabled &&
                'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 active:scale-95',
              !isQualified &&
                !isRecommended &&
                !hasValidEmail &&
                !disabled &&
                'bg-zinc-800 text-zinc-500 hover:bg-zinc-700 hover:text-zinc-400 active:scale-95',
              isAnimating && 'animate-pop',
              className,
            )}
          >
            <Send className="h-3.5 w-3.5" />
            <span>Send Retainer</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-4 border-none rounded-3xl bg-zinc-900" align="end">
          {popoverMode === 'options' ? renderOptionsView() : renderEmailView()}
        </PopoverContent>
      </Popover>

      {/* Preview Modal */}
      {firmId && (
        <RetainerPreviewModal
          open={showPreview}
          onClose={() => setShowPreview(false)}
          firmId={firmId}
          mergeFields={initialMergeFields}
        />
      )}
    </>
  );
};
